import { useState, useEffect, useMemo, useCallback } from 'react'
import { TiCalculator } from "react-icons/ti";
import { FaExclamationTriangle, FaClipboard, FaCheck } from "react-icons/fa";
import { FiAlertTriangle, FiChevronDown, FiChevronUp, FiLock, FiUnlock, FiX, FiRefreshCw, FiInfo } from 'react-icons/fi';
import { IoCubeOutline } from "react-icons/io5";
import axios from 'axios';
import CostBreakdownChart from '../comps/CostBreakdownChat';
import { toast } from 'react-toastify';



// Determine Section 232 material flags from analysis data
const getSec232Flags = (initialValues) => {
  // Priority 1: Explicit sec232Info from HTS classification
  if (initialValues?.sec232Info?.applies) {
    const sec232Material = initialValues.sec232Info.material?.toLowerCase() || '';
    return {
      isSteel: sec232Material === 'steel',
      isAluminum: sec232Material === 'aluminum',
      source: 'hts_classification',
    };
  }

  // Priority 2: Use materialParent from LLM extraction (most accurate)
  const materialParent = initialValues?.materialParent?.toLowerCase() || '';
  if (materialParent === 'steel' || materialParent === 'aluminum') {
    return {
      isSteel: materialParent === 'steel',
      isAluminum: materialParent === 'aluminum',
      source: 'material_analysis',
    };
  }

  // No Section 232 material detected
  return {
    isSteel: false,
    isAluminum: false,
    source: null,
  };
};

export default function TariffCalculator({
  initialValues,
  fileId,
  // Component info for constructive value letter
  partNumber = '',
  material = '',
  weightKg = 0,
}) {
  // Get auto-detected Section 232 flags
  const sec232Flags = getSec232Flags(initialValues);

  const [formData, setFormData] = useState({
    htsCode: initialValues?.htsCode || '',
    coo: '',
    unitCost: 100,
    quantity: 1,
    generalDutyPercent: initialValues?.generalDutyPercent || 0,
    isSteel: sec232Flags.isSteel,
    isAluminum: sec232Flags.isAluminum,
    // Section 232 value overrides (default to full cost if material detected)
    steelValue: sec232Flags.isSteel ? 100 : 0,
    aluminumValue: sec232Flags.isAluminum ? 100 : 0,
    applyTransshipment: false,
    sec301Applies: initialValues?.sec301Info?.applies || false,
    sec301Rate: initialValues?.sec301Info?.ratePercent || 0,
    sec301List: initialValues?.sec301Info?.list || '',
  })

  const [countries, setCountries] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const token = sessionStorage.getItem("userToken");
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // Commit functionality state
  const [committedData, setCommittedData] = useState(null);
  const [isThreatenedOpen, setIsThreatenedOpen] = useState(false);
  const [isLoadingCommitted, setIsLoadingCommitted] = useState(false);
  const [isSavingCommitted, setIsSavingCommitted] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);

  const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;
  const GET_Countries = `${BASE_URL}/ocr/countries/`;
  const CALCULATE_LANDED_COST = `${BASE_URL}/ocr/calculate-landed-cost/`;
  const GENERATE_REPORT = `${BASE_URL}/ocr/generate-report/`;

  // Update form when initialValues changes (e.g., different component selected)
  useEffect(() => {
    const newSec232Flags = getSec232Flags(initialValues);
    setFormData(prev => {
      const totalCost = prev.unitCost * prev.quantity;
      return {
        ...prev,
        htsCode: initialValues?.htsCode || '',
        generalDutyPercent: initialValues?.generalDutyPercent || 0,
        isSteel: newSec232Flags.isSteel,
        isAluminum: newSec232Flags.isAluminum,
        // Reset 232 values based on new material detection
        steelValue: newSec232Flags.isSteel ? totalCost : 0,
        aluminumValue: newSec232Flags.isAluminum ? totalCost : 0,
        sec301Applies: initialValues?.sec301Info?.applies || false,
        sec301Rate: initialValues?.sec301Info?.ratePercent || 0,
        sec301List: initialValues?.sec301Info?.list || '',
      };
    });
    // Clear previous results when switching components
    setResult(null);
    setCommittedData(null);
  }, [initialValues]);

  // Auto-disable Section 301 when non-China country is selected
  useEffect(() => {
    const isChina = formData.coo === 'CN' || formData.coo === 'China';
    if (!isChina && formData.sec301Applies) {
      setFormData(prev => ({ ...prev, sec301Applies: false }));
    }
  }, [formData.coo]);

  // Load committed calculation from backend when fileId is available
  useEffect(() => {
    if (!fileId) return;

    const loadCommittedCalc = async () => {
      try {
        setIsLoadingCommitted(true);
        const res = await axios.get(
          `${BASE_URL}/ocr/committed-calculation/${fileId}/?type=individual`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.success && res.data.committed) {
          const committed = res.data.committed;
          // API returns camelCase: formData, result, timestamp
          setCommittedData({
            formData: committed.formData,
            result: committed.result,
            timestamp: committed.timestamp
          });
          // Also update form data and result to show committed values
          if (committed.formData) {
            setFormData(prev => ({
              ...prev,
              ...committed.formData,
            }));
          }
          if (committed.result) {
            setResult(committed.result);
          }
        }
      } catch (err) {
        // 404 is expected when no committed calc exists
        if (err.response?.status !== 404) {
          console.error('Failed to load committed calculation', err);
        }
      } finally {
        setIsLoadingCommitted(false);
      }
    };

    loadCommittedCalc();
  }, [fileId, BASE_URL, token]);

  // Separate breakdown into main (non-threatened) and threatened items
  const mainBreakdown = useMemo(() =>
    result?.breakdown?.filter(item => item.type !== 'threatened') || []
  , [result]);

  const threatenedBreakdown = useMemo(() =>
    result?.breakdown?.filter(item => item.type === 'threatened') || []
  , [result]);

  const threatenedTotal = useMemo(() =>
    threatenedBreakdown.reduce((sum, item) => sum + (item.cost || 0), 0)
  , [threatenedBreakdown]);

  // Check if committed values differ from current values
  const hasValuesChanged = useMemo(() => {
    if (!committedData) return false;
    const committed = committedData.formData;
    return (
      committed.htsCode !== formData.htsCode ||
      committed.coo !== formData.coo ||
      committed.unitCost !== formData.unitCost ||
      committed.quantity !== formData.quantity ||
      committed.generalDutyPercent !== formData.generalDutyPercent
    );
  }, [committedData, formData]);

  // Calculate total cost and other value for display
  const totalCost = formData.unitCost * formData.quantity;
  const otherValue = Math.max(0, totalCost - formData.steelValue - formData.aluminumValue);

  // Handle unit cost or quantity change - maintain material value ratios
  const handleCostChange = useCallback((newUnitCost, newQuantity) => {
    const oldTotal = formData.unitCost * formData.quantity;
    const newTotal = newUnitCost * newQuantity;

    if (oldTotal > 0 && newTotal > 0) {
      const steelRatio = formData.steelValue / oldTotal;
      const aluminumRatio = formData.aluminumValue / oldTotal;
      setFormData(prev => ({
        ...prev,
        unitCost: newUnitCost,
        quantity: newQuantity,
        steelValue: Math.round(newTotal * steelRatio * 100) / 100,
        aluminumValue: Math.round(newTotal * aluminumRatio * 100) / 100,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        unitCost: newUnitCost,
        quantity: newQuantity,
      }));
    }
  }, [formData]);

  // Reset 232 values to defaults (full cost if material detected)
  const handleReset232Values = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      steelValue: prev.isSteel ? (prev.unitCost * prev.quantity) : 0,
      aluminumValue: prev.isAluminum ? (prev.unitCost * prev.quantity) : 0,
    }));
  }, []);

  // Commit current calculation - save to backend if fileId available
  const handleCommitCalculation = async () => {
    if (!result) return;

    const timestamp = Date.now();

    // If fileId is provided, persist to backend
    if (fileId) {
      try {
        setIsSavingCommitted(true);
        await axios.post(
          `${BASE_URL}/ocr/committed-calculation/${fileId}/`,
          {
            formData: { ...formData },  // camelCase to match backend
            result: { ...result },
            timestamp: timestamp,
            type: 'individual'
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Calculation saved');
      } catch (err) {
        console.error('Failed to save calculation', err);
        toast.error('Failed to save calculation');
        return;
      } finally {
        setIsSavingCommitted(false);
      }
    }

    setCommittedData({
      formData: { ...formData },
      result: { ...result },
      timestamp: timestamp
    });
  };

  // Clear committed data - delete from backend if fileId available
  const handleClearCommit = async () => {
    if (fileId) {
      try {
        await axios.delete(
          `${BASE_URL}/ocr/committed-calculation/${fileId}/?type=individual`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (err) {
        console.error('Failed to clear committed calculation', err);
      }
    }
    setCommittedData(null);
  };

  // Copy report to clipboard
  const handleCopyToClipboard = async () => {
    const dataToExport = committedData || { formData, result };
    if (!dataToExport.result) return;

    try {
      setIsGeneratingReport(true);

      const payload = {
        hts_code: dataToExport.formData.htsCode,
        duty_rate: dataToExport.formData.generalDutyPercent,
        coo: dataToExport.formData.coo,
        unit_cost: dataToExport.formData.unitCost,
        quantity: dataToExport.formData.quantity,
        // Section 232 value overrides
        steel_value: dataToExport.formData.steelValue || 0,
        aluminum_value: dataToExport.formData.aluminumValue || 0,
        is_steel: dataToExport.formData.isSteel,
        is_aluminum: dataToExport.formData.isAluminum,
        // Component info for constructive value letter
        component_info: {
          part_number: partNumber || initialValues?.partNumber || '',
          material: material || initialValues?.material || '',
          weight_kg: weightKg || initialValues?.weightKg || 0,
        },
        calculation_result: dataToExport.result,
        is_committed: !!committedData,
        committed_at: committedData?.timestamp || null,
      };

      const res = await axios.post(
        GENERATE_REPORT,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await navigator.clipboard.writeText(res.data.report);
      setCopySuccess(true);
      toast.success('Report copied to clipboard');
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error('Failed to generate report', err);
      toast.error('Failed to copy report');
    } finally {
      setIsGeneratingReport(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setIsCalculating(true);

      const payload = {
        hts_code: formData.htsCode,
        coo: formData.coo, // e.g. "CN"
        unit_cost: formData.unitCost,
        quantity: formData.quantity,
        general_duty_percent: formData.generalDutyPercent,
        material_info: {
          is_steel: formData.isSteel,
          is_aluminum: formData.isAluminum,
          raw_material: formData.isAluminum
            ? "Aluminum"
            : formData.isSteel
              ? "Steel"
              : "",
          // Section 232 value overrides (for constructive value)
          steel_value: formData.steelValue || 0,
          aluminum_value: formData.aluminumValue || 0,
        },
        sec_301_info: formData.sec301Applies
          ? {
            applies: true,
            rate_percent: formData.sec301Rate,
            list: formData.sec301List,
          }
          : { applies: false },
        apply_transshipment: formData.applyTransshipment,
      };

      const res = await axios.post(CALCULATE_LANDED_COST, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setResult(res.data);
    } catch (err) {
      console.error("Calculation failed", err);
    } finally {
      setIsCalculating(false);
    }
  };
const fetchCountries = async () => {
  try {
    const res = await axios.get(GET_Countries, {
      headers: { Authorization: `Bearer ${token}` }
    });

    setCountries(res.data.countries || []);
  } catch (err) {
    console.error("Failed to fetch countries", err);
  }
};


  useEffect(() => {
    fetchCountries();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value)
  }


  return (
    <div className="space-y-6">
      {/* Loading committed calculation indicator */}
      {isLoadingCommitted && (
        <div className="px-4 py-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-2 text-blue-700">
            <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm font-medium">Loading saved calculation...</span>
          </div>
        </div>
      )}

      {/* Form */}
      <div className=" bg-white rounded-xl shadow-sm border border-gray-300
           overflow-hidden p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TiCalculator className="w-5 h-5 text-[#1e5045]" />
          Landed Cost Calculator
        </h3>

        <div className="grid grid-cols-2 gap-4">
          {/* HTS Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              HTS Code
            </label>
            <input
              type="text"
              value={formData.htsCode}
              onChange={(e) => setFormData({ ...formData, htsCode: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200 font-mono"
              placeholder="XXXX.XX.XXXX"
            />
          </div>

          {/* Country of Origin */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Country of Origin
            </label>
            <select
              value={formData.coo}
              onChange={(e) => setFormData({ ...formData, coo: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200"
            >
              <option value="">Select Country</option>

              {loadingCountries && (
                <option disabled>Loading...</option>
              )}

              {countries.map((country) => {
                // If API returns objects
                if (typeof country === "object") {
                  return (
                    <option key={country.code} value={country.code}>
                      {country.name} ({country.code})
                    </option>
                  );
                }

                return (
                  <option key={country} value={country}>
                    {country}
                  </option>
                );
              })}
            </select>

          </div>

          {/* Unit Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Unit Cost (USD)
            </label>
            <input
              type="number"
              value={formData.unitCost}
              onChange={(e) =>
                setFormData({ ...formData, unitCost: parseFloat(e.target.value) || 0 })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200"
              min="0"
              step="0.01"
            />
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Quantity
            </label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) =>
                setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200"
              min="1"
            />
          </div>

          {/* General Duty Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              General Duty Rate (%)
            </label>
            <input
              type="number"
              value={formData.generalDutyPercent}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  generalDutyPercent: parseFloat(e.target.value) || 0,
                })
              }
              className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200"
              min="0"
              step="0.1"
            />
          </div>

          {/* Material Type */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Section 232 Material
              </label>
              {(sec232Flags.isSteel || sec232Flags.isAluminum) && (
                <span className="text-xs text-[#94c43d] font-medium">
                  Auto-detected from analysis
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isSteel}
                  onChange={(e) =>
                    setFormData({ ...formData, isSteel: e.target.checked })
                  }
                  className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045]"
                />
                <span className="text-sm">Steel</span>
                {sec232Flags.isSteel && (
                  <span className="text-xs bg-[#94c43d]/20 text-[#1e5045] px-1.5 py-0.5 rounded">
                    Detected
                  </span>
                )}
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.isAluminum}
                  onChange={(e) =>
                    setFormData({ ...formData, isAluminum: e.target.checked })
                  }
                  className="rounded border-gray-300 text-[#1e5045] focus:ring-[#1e5045]"
                />
                <span className="text-sm">Aluminum</span>
                {sec232Flags.isAluminum && (
                  <span className="text-xs bg-[#94c43d]/20 text-[#1e5045] px-1.5 py-0.5 rounded">
                    Detected
                  </span>
                )}
              </label>
            </div>
            {initialValues?.material && (
              <p className="text-xs text-gray-500 mt-1">
                Raw material: {initialValues.material}
              </p>
            )}
          </div>

          {/* Section 232 Value Override - Only show if material is selected */}
          {(formData.isSteel || formData.isAluminum) && (
            <div className="col-span-2 border-2 border-dashed border-[#1e5045]/30 rounded-lg p-4 bg-[#f6f4ed]">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                    <IoCubeOutline className="w-5 h-5 text-[#1e5045]" />
                    Section 232 Value Override
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    Specify the raw material cost (not the full component cost) for 232 tariff calculation
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleReset232Values}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#1e5045] hover:bg-[#1e5045]/10 rounded-lg transition-colors"
                >
                  <FiRefreshCw className="w-4 h-4" />
                  Reset to Full Cost
                </button>
              </div>

              <div className="grid grid-cols-3 gap-4">
                {/* Steel Value */}
                {formData.isSteel && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Steel Value (USD)
                    </label>
                    <input
                      type="number"
                      value={formData.steelValue}
                      onChange={(e) => setFormData({ ...formData, steelValue: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                      min="0"
                      step="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Subject to 50% Section 232
                    </p>
                  </div>
                )}

                {/* Aluminum Value */}
                {formData.isAluminum && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Aluminum Value (USD)
                    </label>
                    <input
                      type="number"
                      value={formData.aluminumValue}
                      onChange={(e) => setFormData({ ...formData, aluminumValue: parseFloat(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e5045] focus:border-transparent"
                      min="0"
                      step="1"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Subject to 50% Section 232
                    </p>
                  </div>
                )}

                {/* Other Value (calculated) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Other Value (USD)
                  </label>
                  <input
                    type="number"
                    value={otherValue}
                    disabled
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg bg-gray-100 text-gray-600"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Auto-calculated from total
                  </p>
                </div>
              </div>

              {/* Info about constructive value */}
              <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
                <FiInfo className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Constructive Value:</span>{' '}
                  If you don't know the exact raw material cost, use "Copy Report" to generate a letter template to request this information from your manufacturer.
                </p>
              </div>
            </div>
          )}

          {/* Section 301 */}
          <div className="col-span-2">
            {(() => {
              const isChina = formData.coo === 'CN' || formData.coo === 'China';
              return (
                <>
                  <label className={`flex items-center gap-2 mb-2 ${!isChina ? 'opacity-50' : ''}`}>
                    <input
                      type="checkbox"
                      checked={formData.sec301Applies}
                      disabled={!isChina}
                      onChange={(e) =>
                        setFormData({ ...formData, sec301Applies: e.target.checked })
                      }
                      className="rounded border-gray-300 text-[#f26522] focus:ring-[#f26522] disabled:cursor-not-allowed"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Section 301 (China) Applies
                    </span>
                    {!isChina && (
                      <span className="text-xs text-gray-400 italic">
                        (Only applies to China)
                      </span>
                    )}
                  </label>
            {formData.sec301Applies && isChina && (
              <div className="flex items-center gap-4 ml-6">
                <input
                  type="number"
                  value={formData.sec301Rate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sec301Rate: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200 "
                  placeholder="Rate %"
                />
                <input
                  type="text"
                  value={formData.sec301List}
                  onChange={(e) =>
                    setFormData({ ...formData, sec301List: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg
           focus:outline-none focus:ring-2 focus:ring-so-green-500
           focus:border-transparent transition-colors duration-200"
                  placeholder="List (e.g., List 3)"
                />
              </div>
            )}
                </>
              );
            })()}
          </div>

          {/* Transshipment */}
          <div className="col-span-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.applyTransshipment}
                onChange={(e) =>
                  setFormData({ ...formData, applyTransshipment: e.target.checked })
                }
                className="rounded border-gray-300 text-orange-500 focus:ring-orange-500"
              />
              <span className="text-sm font-medium text-gray-700">
                Apply Transshipment Penalty (40%)
              </span>
            </label>
          </div>
        </div>
        <button
          onClick={handleCalculate}
          disabled={isCalculating || !formData.htsCode}
          className="px-4 py-2 bg-[#1e5045] text-white font-medium rounded-lg
   hover:bg-[#163d35] focus:outline-none focus:ring-2
   focus:ring-[#1e5045] focus:ring-offset-2
   transition-colors duration-200 w-full mt-6"
        >
          {isCalculating ? "Calculating..." : "Calculate Landed Cost"}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className=" bg-white rounded-xl shadow-sm border border-gray-300
           overflow-hidden p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Calculation Results
          </h3>


          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="p-4 bg-[#f6f4ed] rounded-lg">
              <div className="text-sm text-gray-500">Base Cost</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(result.base_cost)}
              </div>
            </div>
            <div className="p-4 bg-[#1e5045] rounded-lg text-white">
              <div className="text-sm opacity-80">Landed Cost</div>
              <div className="text-2xl font-bold">
                {formatCurrency(result.landed_cost)}
              </div>
            </div>
            <div className="p-4 bg-[#f26522]/15 rounded-lg">
              <div className="text-sm text-[#f26522]">Total Tariffs</div>
              <div className="text-2xl font-bold text-[#f26522]">
                {formatCurrency(result.total_tariff)}
              </div>
            </div>
            <div className="p-4 bg-[#f6f4ed] rounded-lg">
              <div className="text-sm text-gray-500">Effective Rate</div>
              <div className="text-2xl font-bold text-gray-900">
                {((result.total_tariff / result.base_cost) * 100).toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Cost Breakdown Chart */}
          <div className="mb-6">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Cost Breakdown</h4>
            <CostBreakdownChart
              baseCost={result.base_cost}
              generalDuty={result.breakdown.find((b) => b.name === 'General Duty')?.cost || 0}
              sec232={result.breakdown.find((b) => b.name.includes('232'))?.cost}
              sec301={result.breakdown.find((b) => b.name.includes('301'))?.cost}
              transshipment={result.breakdown.find((b) => b.name.includes('Transshipment'))?.cost}
              landedCost={result.landed_cost}
            />
          </div>

          {/* Main Breakdown Table (excludes threatened) */}
          <div className="overflow-hidden border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Item
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Rate
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {mainBreakdown.map((item, idx) => (
                  <tr
                    key={idx}
                    className={
                      item.type === "waived"
                        ? "bg-gray-50 text-gray-400"
                        : item.is_danger
                          ? "bg-red-50"
                          : ""
                    }
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {item.is_danger && (
                          <FaExclamationTriangle className="w-4 h-4 text-red-500" />
                        )}
                        <span
                          className={`font-medium ${item.type === "waived"
                            ? "line-through"
                            : item.is_danger
                              ? "text-red-700"
                              : "text-gray-900"
                            }`}
                        >
                          {item.name}
                        </span>
                      </div>
                      {item.note && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {item.note}
                        </div>
                      )}
                    </td>

                    <td className="px-4 py-3 text-right font-mono text-sm">
                      {item.rate || "—"}
                    </td>

                    <td
                      className={`px-4 py-3 text-right font-mono text-sm ${item.is_danger ? "text-red-700 font-semibold" : ""
                        }`}
                    >
                      {item.type === "waived" ? "—" : formatCurrency(item.cost)}
                    </td>
                  </tr>
                ))}
              </tbody>

              <tfoot className="bg-[#1e5045] text-white">
                <tr>
                  <td className="px-4 py-3 font-semibold">Total Landed Cost</td>
                  <td></td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg">
                    {formatCurrency(result.landed_cost)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Threatened Tariffs - Collapsible FYI Section */}
          {threatenedBreakdown.length > 0 && (
            <div className="mt-4 border-2 border-[#f26522]/30 rounded-lg overflow-hidden bg-[#f26522]/10">
              {/* Collapsible Header */}
              <button
                onClick={() => setIsThreatenedOpen(!isThreatenedOpen)}
                className="w-full px-4 py-3 flex items-center justify-between hover:bg-[#f26522]/20 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <FiAlertTriangle className="w-5 h-5 text-[#f26522]" />
                  <span className="font-semibold text-[#f26522]">
                    Potential Future Tariffs (FYI)
                  </span>
                  <span className="px-2 py-0.5 bg-[#f26522]/20 text-[#f26522] text-xs rounded-full font-medium">
                    {threatenedBreakdown.length} item{threatenedBreakdown.length > 1 ? 's' : ''}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-[#f26522] font-semibold">
                    +{formatCurrency(threatenedTotal)}
                  </span>
                  {isThreatenedOpen ? (
                    <FiChevronUp className="w-5 h-5 text-[#f26522]" />
                  ) : (
                    <FiChevronDown className="w-5 h-5 text-[#f26522]" />
                  )}
                </div>
              </button>

              {/* Expanded Content */}
              {isThreatenedOpen && (
                <div className="border-t border-[#f26522]/30">
                  <div className="px-4 py-2 bg-[#f26522]/15 text-sm text-gray-700">
                    These tariffs are proposed or threatened but not yet implemented.
                    Included for planning purposes only.
                  </div>
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-[#f26522]/20">
                      {threatenedBreakdown.map((item, idx) => (
                        <tr key={idx} className="bg-[#f26522]/5">
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-800">
                              {item.name}
                            </div>
                            {item.note && (
                              <div className="text-xs text-gray-600 mt-0.5">{item.note}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-mono text-sm">
                            {item.rate}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-[#f26522] font-mono text-sm">
                            {formatCurrency(item.cost)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#f26522]/20">
                      <tr>
                        <td className="px-4 py-2 font-semibold text-[#f26522]">
                          POTENTIAL ADDITIONAL
                        </td>
                        <td></td>
                        <td className="px-4 py-2 text-right font-bold text-[#f26522] font-mono">
                          {formatCurrency(threatenedTotal)}
                        </td>
                      </tr>
                      <tr>
                        <td className="px-4 py-2 font-semibold text-[#f26522]">
                          POTENTIAL TOTAL (if all enacted)
                        </td>
                        <td></td>
                        <td className="px-4 py-2 text-right font-bold text-[#f26522] font-mono">
                          {formatCurrency(result.landed_cost + threatenedTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Applied Tariffs */}
          {result.applied_tariffs.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {result.applied_tariffs.map((tariff) => (
                <span key={tariff} className="inline-flex items-center px-2.5 py-0.5 rounded-full
           text-xs font-medium bg-[#f26522]/15 text-[#f26522]">
                  {tariff}
                </span>
              ))}
            </div>
          )}

          {/* Commit Calculation Section */}
          <div className="mt-4 flex flex-col gap-3">
            {/* Committed Status Indicator */}
            {committedData && (
              <div className={`flex items-center justify-between px-4 py-3 rounded-lg ${
                hasValuesChanged
                  ? 'bg-[#f26522]/10 border-2 border-[#f26522]/40'
                  : 'bg-[#94c43d]/15 border-2 border-[#94c43d]/40'
              }`}>
                <div className="flex items-center gap-3">
                  <FiLock className={`w-5 h-5 ${hasValuesChanged ? 'text-[#f26522]' : 'text-[#94c43d]'}`} />
                  <div>
                    <span className={`font-semibold ${hasValuesChanged ? 'text-[#f26522]' : 'text-[#1e5045]'}`}>
                      Calculation Committed
                    </span>
                    <span className="text-gray-500 text-sm ml-2">
                      {new Date(committedData.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  {hasValuesChanged && (
                    <span className="px-2 py-1 bg-[#f26522]/20 text-[#f26522] text-xs rounded-full font-medium">
                      Values changed
                    </span>
                  )}
                </div>
                <button
                  onClick={handleClearCommit}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  <FiX className="w-4 h-4" />
                  Clear
                </button>
              </div>
            )}

            {/* Commit Button */}
            {!committedData && (
              <button
                onClick={handleCommitCalculation}
                disabled={isSavingCommitted}
                className="w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2
                  bg-gray-100 text-gray-700 hover:bg-gray-200 border-2 border-gray-300 disabled:opacity-50"
              >
                <FiUnlock className="w-5 h-5" />
                {isSavingCommitted ? 'Saving...' : 'Commit Calculation'}
              </button>
            )}

            {/* Copy Report to Clipboard Button */}
            <button
              onClick={handleCopyToClipboard}
              disabled={isGeneratingReport}
              className={`w-full py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${
                copySuccess
                  ? 'bg-[#94c43d] text-white border-2 border-[#94c43d]'
                  : 'bg-[#1e5045]/10 text-[#1e5045] hover:bg-[#1e5045]/20 border-2 border-[#1e5045]/30'
              } disabled:opacity-50`}
            >
              {copySuccess ? (
                <>
                  <FaCheck className="w-4 h-4" />
                  Copied to Clipboard
                </>
              ) : isGeneratingReport ? (
                'Generating Report...'
              ) : (
                <>
                  <FaClipboard className="w-4 h-4" />
                  Copy Report to Clipboard
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
